// AboutSection.jsx
// Inspired by CAPPC's three-panel layout: About Us | Leadership Team | Work With Us
// Adapted for Greatcalcy with richer visual treatment.

import { BarChart2, Briefcase, ChevronRight, Users } from "lucide-react";
import StatCard from "./StatCard";

const stats = [
  { value: "17+", label: "Years" },
  { value: "3", label: "Countries" },
  { value: "100+", label: "Clients" },
  { value: "E2E", label: "Support" },
];

const panels = [
  {
    icon: Briefcase,
    tag: "WHO WE ARE",
    title: "About Us",
    body: `Greatcalcy is a cross-border accounting and tax consultancy operating across India, Canada, and the USA. Founded on the principle of bringing institutional-grade financial services to small and medium enterprises, we offer the agility of a local partner with the breadth of a global firm.`,
    cta: { label: "Read More", href: "#about" },
    accent: "bg-gold/10 border-gold/20",
    iconColor: "text-gold",
  },
  {
    icon: Users,
    tag: "OUR PEOPLE",
    title: "Leadership Team",
    body: `Our team is led by Prashant — a highly motivated Tax & Bookkeeping professional with 17 years of cross-functional experience in tax planning, audit, GST/TDS, and cloud-based bookkeeping across Indian and Canadian tax jurisdictions.`,
    cta: { label: "Meet the Team", href: "#about" },
    accent: "bg-navy/5 border-navy/10",
    iconColor: "text-navy",
  },
  {
    icon: BarChart2,
    tag: "JOIN US",
    title: "Work With Us",
    body: `We nurture a culture of ownership and professional growth. If you're a driven accounting or finance professional looking to work across global jurisdictions, we'd love to hear from you. Apply and meet us for an interview.`,
    cta: { label: "Apply Now", href: "#contact" },
    accent: "bg-surface-container border-outline-variant/30",
    iconColor: "text-on-surface-variant",
  },
];

function AboutPanel({ icon, tag, title, body, cta, accent, iconColor }) {
  const Icon = icon; // Convert icon name to component
  return (
    <div className={`flex flex-col gap-5 p-8 rounded-2xl border ${accent}`}>
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-white shadow-sm`}>
        <span className={`material-symbols-outlined text-2xl ${iconColor}`}><Icon /></span>
      </div>
      <div>
        <p className="font-manrope text-label-caps text-on-surface-variant mb-1">{tag}</p>
        <h3 className="font-manrope text-h2 text-navy">{title}</h3>
      </div>
      <p className="font-manrope text-body-md text-on-surface-variant leading-relaxed flex-1">
        {body}
      </p>
      {/* <a
        href={cta.href}
        className="inline-flex items-center gap-1 font-manrope font-bold text-sm text-gold hover:gap-3 transition-all"
      >
        {cta.label}
        <span className="material-symbols-outlined text-sm"><ChevronRight /></span>
      </a> */}
    </div>
  );
}

export default function AboutSection() {
  return (
    <section className="py-20 max-w-[1280px] mx-auto px-6" id="about">
      {/* Section header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px w-8 bg-gold" />
            <span className="font-manrope text-label-caps text-gold">OUR STORY</span>
          </div>
          <h2 className="font-manrope text-h1 text-navy">
            Expertise That Bridges Continents
          </h2>
        </div>

      </div>

      {/* Main two-column */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start mb-16">
        {/* Image + badge */}
        <div className="lg:col-span-4 relative">
          <div className="md:aspect-[4/5] rounded-2xl overflow-hidden shadow-xl border-4 border-white">
            <img
              alt="Prashant - Founder, Greatcalcy"
              className="w-full h-1/2 md:h-full object-cover"
              src="https://images.openai.com/static-rsc-4/fCqurWv94-SEFJQa-Q9kxKsMsMRRjq-CSHWAHVglQUKJZjq2yeDu72d14fZMSIY9caTyObkycHk_GcKC4mYukjcHrFmKAIuBncA_A3deFgt7TnMp_bHAZuVfSJp75d566tMxwVrttTJVIfbD8kGhK-ieHNYpfs-fK_EZgTu2jSatwNE-4wqavhYnoX5lWsaU?purpose=fullsize"
            />
          </div>
          <div className="absolute -bottom-6 -right-6 bg-secondary-container p-6 rounded-2xl shadow-xl hidden md:block">
            <span className="font-manrope text-[36px] font-extrabold text-on-secondary-container block leading-none">
              {"17+"}
            </span>
            <span className="font-manrope text-label-caps text-on-secondary-container">
              Years Experience
            </span>
          </div>
        </div>

        {/* Stats + three panels */}
        <div className="lg:col-span-8 flex flex-col gap-8">


          {/* CAPPC-style three panels */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {panels.map((p) => (
              <AboutPanel key={p.title} {...p} />
            ))}
          </div>
        </div>
        <div className="lg:col-span-12 flex flex-col gap-8">
          {/* Stats row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {stats.map((s) => (
              <StatCard key={s.label} value={s.value} label={s.label} />
            ))}
          </div>
        </div>
      </div>
    </section >
  );
}
